import {humanizeKey} from '../../core/client/template-fields.core.js';
import {DEFAULT_LAYOUT_CONTEXT, LAYOUT_PRESENTATION} from '../../core/vocabulary.js';
import {escapeHtml} from '../../core/escape-html.js';

/**
 * @typedef {Object} LayoutContextEditorOptions
 * @property {string[]} contexts layout contexts to render an editor for
 * @property {{key: string, label: string}[]} checkboxFields fields selectable as layout toggle
 * @property {Map<string, string>} enabledByField selected toggle field per context, already limited to `checkboxFields`
 * @property {Map<string, string>} presentationByContext
 * @property {Map<string, number | string>} columnsByContext
 * @property {(context: string, fieldKey: string) => void} onEnabledByFieldChange
 * @property {(context: string, presentation: string) => void} onPresentationChange
 * @property {(context: string, columns: number) => void} onColumnsChange
 */

/**
 * Renders, per layout context, the toggle field select, the editor presentation select and the
 * desktop column count input into `container`, replacing its content. The column count is hidden
 * while the carousel presentation is selected.
 *
 * @param {HTMLElement} container
 * @param {LayoutContextEditorOptions} options
 */
export function renderLayoutContextEditors(container, options) {
    container.innerHTML = '';
    for (const context of options.contexts) {
        const contextLabel = context === DEFAULT_LAYOUT_CONTEXT ? 'Homepage' : humanizeKey(context);
        container.appendChild(toggleFieldGroup(context, contextLabel, options));
        const columnsGroup = columnsInputGroup(context, contextLabel, options);
        container.appendChild(presentationGroup(context, contextLabel, columnsGroup, options));
        container.appendChild(columnsGroup);
    }
}

function toggleFieldGroup(context, contextLabel, {checkboxFields, enabledByField, onEnabledByFieldChange}) {
    const group = document.createElement('div');
    group.className = 'form-group';

    const label = document.createElement('label');
    label.setAttribute('for', `layout-enabled-by-field-${context}`);
    label.innerHTML = `${escapeHtml(contextLabel)} Layout Toggle Field <small>(optional checkbox controlling ${escapeHtml(context)} grid participation)</small>`;

    const select = document.createElement('select');
    select.name = `layoutEnabledByField${context}`;
    select.id = `layout-enabled-by-field-${context}`;
    select.innerHTML = '<option value="">No layout toggle</option>' + checkboxFields
        .map(({key, label: optionLabel}) => `<option value="${escapeHtml(key)}">${escapeHtml(optionLabel)} (${escapeHtml(key)})</option>`)
        .join('');
    select.value = enabledByField.get(context) || '';
    select.addEventListener('change', () => onEnabledByFieldChange(context, select.value));

    group.appendChild(label);
    group.appendChild(select);
    return group;
}

function presentationGroup(context, contextLabel, columnsGroup, {presentationByContext, onPresentationChange}) {
    const group = document.createElement('div');
    group.className = 'form-group';
    const label = document.createElement('label');
    label.setAttribute('for', `layout-presentation-${context}`);
    label.textContent = `${contextLabel} editor layout`;
    const select = document.createElement('select');
    select.id = `layout-presentation-${context}`;
    select.innerHTML = `<option value="${LAYOUT_PRESENTATION.GRID}">Grid placement</option>`
        + `<option value="${LAYOUT_PRESENTATION.CAROUSEL}">Carousel order</option>`;
    select.value = presentationByContext.get(context) || LAYOUT_PRESENTATION.GRID;
    columnsGroup.hidden = select.value === LAYOUT_PRESENTATION.CAROUSEL;
    select.addEventListener('change', () => {
        onPresentationChange(context, select.value);
        columnsGroup.hidden = select.value === LAYOUT_PRESENTATION.CAROUSEL;
    });
    group.append(label, select);
    return group;
}

function columnsInputGroup(context, contextLabel, {columnsByContext, onColumnsChange}) {
    const group = document.createElement('div');
    group.className = 'form-group';
    const label = document.createElement('label');
    label.setAttribute('for', `layout-columns-${context}`);
    label.textContent = `${contextLabel} desktop grid columns`;
    const input = document.createElement('input');
    input.id = `layout-columns-${context}`;
    input.type = 'number';
    input.min = '1';
    input.max = '10';
    input.step = '1';
    input.placeholder = 'Automatic';
    input.value = String(columnsByContext.get(context) || '');
    input.addEventListener('change', () => onColumnsChange(context, Number(input.value)));
    const help = document.createElement('small');
    help.className = 'layout-columns-help';
    help.textContent = 'Match the website grid. This controls card positions in the editor and published fragments; the website CSS sets the visible columns.';
    group.append(label, input, help);
    return group;
}

/**
 * @param {{key: string, label: string}[]} checkboxFields
 * @returns {string} options of the search engine indexing select
 */
export function indexedByFieldOptionsMarkup(checkboxFields) {
    return '<option value="">Always indexed</option>' + checkboxFields
        .map(({key, label}) => `<option value="${escapeHtml(key)}">Only when '${escapeHtml(label)}' is checked</option>`)
        .join('');
}
